import { useEffect, useState } from 'react';

/**
 * Custom hook for managing localStorage state
 *
 * @param key - The localStorage key
 * @param initialValue - The initial value if key doesn't exist in localStorage
 * @returns A tuple of [storedValue, setValue, removeValue] similar to useState with an additional remove function
 *
 * @example
 * const [name, setName, removeName] = useLocalStorage('userName', 'Guest');
 * // Remove the value: removeName();
 */
export function useLocalStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      if (item) {
        return JSON.parse(item);
      }

      return initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage) return;
      if (event.key !== key) return;

      try {
        const nextValue = event.newValue ? JSON.parse(event.newValue) : initialValue;
        setStoredValue(nextValue);
      } catch (error) {
        console.error(`Error reading localStorage key "${key}":`, error);
        setStoredValue(initialValue);
      }
    };

    const handleCustomStorage = (event: Event) => {
      const customEvent = event as CustomEvent<{ key?: string; value?: T }>;
      if (!customEvent.detail || customEvent.detail.key !== key) return;
      setStoredValue(customEvent.detail.value ?? initialValue);
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('local-storage', handleCustomStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('local-storage', handleCustomStorage);
    };
  }, [initialValue, key]);

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;

      setStoredValue(valueToStore);

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
        window.dispatchEvent(new CustomEvent('local-storage', { detail: { key, value: valueToStore } }));
      }
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  const removeValue = () => {
    try {
      setStoredValue(initialValue);

      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(key);
        window.dispatchEvent(new CustomEvent('local-storage', { detail: { key, value: initialValue } }));
      }
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
    }
  };

  return { data: storedValue, setValue, removeValue } as const;
}
